<template>
  <div class="seminar-registration-fi">
    <div
      v-if="!registrationSuccess"
      class="seminar-registration-fi__registration"
    >
      <grid-container-fi>
        <div class="seminar-registration-fi__headline">
          {{ assignment.title }}
        </div>

        <div class="seminar-registration-fi__event-info">
          <div class="seminar-registration-fi__event-info__seminars">
            <div
              v-for="event in currentEvents"
              :key="event.id"
              class="seminar-registration-fi__event-info__seminar"
            >
              <div
                v-if="currentEvents.length > 1"
                class="seminar-registration-fi__event-info__title"
              >
                {{ event.seminarTitle }}
              </div>
              <div class="seminar-registration-fi__event-info__section">
                <icon-fi
                  icon="home"
                  class="seminar-registration-fi__event-info__icon"
                />
                <div class="seminar-registration-fi__event-info__data">
                  <div v-if="event.location?.type">
                    {{ event.location?.type }}
                  </div>
                  <div v-if="event.location?.street">
                    {{ event.location.street }}
                  </div>
                  <div v-if="event.location?.city">
                    {{ event.location.postcode }} {{ event.location?.city }}
                  </div>
                  <div v-if="event.location?.region">
                    {{ event.location.region }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="seminar-registration-fi__event-info__language">
            <div class="seminar-registration-fi__event-info__section">
              <div
                v-if="currentEvents[0]?.language"
                class="seminar-registration-fi__event-info__section"
              >
                <icon-fi
                  icon="globe"
                  class="seminar-registration-fi__event-info__icon"
                />
                <div class="seminar-registration-fi__event-info__data">
                  {{ languageDisplayName(currentEvents[0].language) }}
                </div>
              </div>
            </div>
          </div>

          <div class="seminar-registration-fi__event-info__participants">
            <div class="seminar-registration-fi__event-info__section">
              <icon-fi
                icon="users"
                class="seminar-registration-fi__event-info__icon"
              />
              <div class="seminar-registration-fi__event-info__data">
                <div class="seminar-registration-fi__event-info__data__row">
                  <div>{{ fields.registrationMaxParticipantsLabel?.value }}</div>
                  <div>{{ currentEvents[0]?.maxParticipants }}</div>
                </div>
                <div class="seminar-registration-fi__event-info__data__row">
                  <div>{{ fields.registrationFreeParticipantsLabel?.value }}</div>
                  <div>
                    {{ currentEvents[0].maxParticipants - currentEvents[0].actualParticipants }}
                  </div>
                </div>
              </div>
            </div>

            <div class="seminar-registration-fi__event-info__actions">
              <button-fi
                class="button-fi__primary button-fi--default-large button-fi--stretched"
                :disabled="
                  !activeEventId || registerUsersLoading || !seminarRegistrationStore.isFormValid
                "
                @click="sendRegistration"
              >
                {{ fields.registrationRegisterBtn?.value }}
              </button-fi>

              <button-fi
                class="button-fi__tertiary button-fi--default-large button-fi--stretched"
                :disabled="registerUsersLoading"
                @click="cancelRegistration"
              >
                {{ fields.registrationCancelBtn?.value }}
              </button-fi>
            </div>

            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="seminarDeadlineLabel"
              class="seminar-registration-fi__event-info__deadline"
              v-html="seminarDeadlineLabel"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>

        <div class="seminar-registration-fi__event-form">
          <div class="seminar-registration-fi__event-form__headline">
            {{ fields.registrationParticipantsTitle?.value }}
          </div>
          <div class="seminar-registration-fi__event-form__participants">
            <seminar-registration-participant-fi
              v-for="i in seminarRegistrationStore.totalParticipants"
              :key="i"
              :index="i"
            />
          </div>
        </div>

        <div class="seminar-registration-fi__events">
          <div class="seminar-registration-fi__events__title">
            {{ fields.registrationEventsTitle?.value }}
          </div>

          <seminar-registration-event-fi
            v-for="event in allEvents"
            :key="event.id"
            :event="event"
            :active="event.id === activeEventId"
            @clicked="currentId = event.id"
          />
        </div>
      </grid-container-fi>
    </div>
    <div v-else>
      <grid-container-fi>
        <result-status-fi class="seminar-registration-fi__success">
          <div class="seminar-registration-fi__success__headline">
            {{ fields.registrationSuccessHeadline?.value }}
          </div>
          <button-fi
            class="button-fi__primary button-fi--default-large"
            @click="exitRegistration"
          >
            {{ fields.registrationSuccessBackBtn?.value }}
          </button-fi>
        </result-status-fi>
      </grid-container-fi>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@urql/vue';
import { ButtonFi, IconFi } from 'atoms';
import { useSeminarRegistrationStore } from 'store/seminar-registration';
import { computed, ref } from 'vue';

import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import ResultStatusFi from '@/components/result-status-fi/ResultStatusFi.vue';
import SeminarRegistrationEventFi from '@/components/seminar-assignments-fi/seminar-registration-fi/seminar-registration-event-fi/SeminarRegistrationEventFi.vue';
import SeminarRegistrationParticipantFi from '@/components/seminar-assignments-fi/seminar-registration-fi/seminar-registration-participant-fi/SeminarRegistrationParticipantFi.vue';
import { useFields } from '@/composables/fields';
import REGISTER_SEMINAR_USERS from '@/graphql/registerSeminarUsers.gql';
import type { SingleLineTextField } from '@/sitecoreFieldTypes';
import { useNotificationStore } from '@/store/pinia/notification';
import { getSelectList } from '@/utils/fieldTransformations';

interface Fields {
  registrationRegisterBtn?: SingleLineTextField;
  registrationCancelBtn?: SingleLineTextField;
  registrationMaxParticipantsLabel?: SingleLineTextField;
  registrationFreeParticipantsLabel?: SingleLineTextField;
  registrationEventsTitle?: SingleLineTextField;
  registrationParticipantsTitle?: SingleLineTextField;
  seminarDeadlineLabel?: SingleLineTextField;
  salutationList?: any;
  titleList?: any;
  countryList?: any;
  registrationSuccessHeadline?: SingleLineTextField;
  registrationSuccessBackBtn?: SingleLineTextField;
  registrationUserAlreadyAssigned?: SingleLineTextField;
  registrationMaxParticipantReached?: SingleLineTextField;
  registrationAddParticipantBtn?: SingleLineTextField;
}

type GetSelectListFields = Fields & { [key: string]: any };

defineOptions({
  name: 'SeminarRegistrationFi',
});

const notificationStore = useNotificationStore();
const seminarRegistrationStore = useSeminarRegistrationStore();

const props = defineProps<{
  assignment: any;
  activeId: string;
}>();

const emits = defineEmits<{
  cancel: [value: void];
  exit: [value: void];
}>();

const fields = useFields<Fields>();

const currentId = ref<string | null>(null);
const registrationSuccess = ref<boolean>(false);

const activeEventId = computed(() => currentId.value || props.activeId);

const allEvents = computed(() => {
  const events = [];

  if (props.assignment.events.length) {
    events.push(...props.assignment.events);
  }
  if (props.assignment.innings.length) {
    events.push(...props.assignment.innings);
  }

  return events;
});

const currentEvents = computed(() => {
  const events = [];
  if (props.assignment.events?.length) {
    const event =
      props.assignment.events.find((event: any) => event.id === activeEventId.value) ||
      props.assignment.events[0];
    events.push(event);
  }

  if (props.assignment.innings?.length) {
    const inning =
      props.assignment.innings.find((event: any) => event.id === activeEventId.value) ||
      props.assignment.innings[0];

    if (inning?.seminars?.length) {
      inning.seminars?.forEach((seminar: any) => {
        const seminarTitle = seminar.title;
        seminar.events.forEach((event: any) => {
          events.push({ ...event, seminarTitle });
        });
      });
    }
  }

  return events;
});

const seminarDeadlineLabel = computed(() => {
  if (fields.seminarDeadlineLabel?.value) {
    return fields.seminarDeadlineLabel.value.replace(
      '{0}',
      `<span class="fi-semibold">${
        currentEvents?.value?.[0]?.location?.type === 'onsite'
          ? currentEvents?.value?.[0]?.deadline
          : currentEvents?.value?.[0]?.start?.day
      }</span>`,
    );
  }
  return null;
});

const titleList = computed(() => {
  const [, titleList] = getSelectList(fields as GetSelectListFields, 'titleList');
  return titleList;
});

const countryList = computed(() => {
  const [, countryList] = getSelectList(fields as GetSelectListFields, 'countryList');
  return countryList;
});

const languageDisplayName = (iso: string) => {
  return (
    countryList.value?.find((country: any) => country.value?.toLowerCase() === iso)?.name || iso
  );
};

const { executeMutation, fetching: registerUsersLoading } = useMutation(REGISTER_SEMINAR_USERS);

const sendRegistration = async () => {
  const users = seminarRegistrationStore.participants.map((participant) => {
    const { id, firstname, lastname, salutation, title, email, phone, myfischer } = participant;
    const { street, streetNumber, zipcode, city, country } = seminarRegistrationStore.address;

    //gender of the user. Possible values: 'n' (for none), 'm' or 'f'
    let gender = 'n';
    if (salutation === 'mrs' || salutation === 'mr') {
      gender = salutation === 'mrs' ? 'f' : 'm';
    }

    const titleLabel = title ? titleList.value.find((el: any) => el.value === title)?.title : '';
    const countryLabel = country
      ? countryList.value.find((el: any) => el.value === country)?.title
      : '';

    return {
      id,
      myfischer,
      firstname,
      lastname,
      gender,
      title: titleLabel,
      email,
      phone,
      company: seminarRegistrationStore.company,
      street,
      streetNumber,
      postcode: zipcode,
      city,
      country: countryLabel,
    };
  });

  const r = await executeMutation({
    assignmentId: props.assignment.assignmentId,
    eventId: activeEventId.value,
    primaryUsergroup: '1620723989689_1',
    users,
  });

  if (r.error) {
    if (r.error.graphQLErrors?.[0]?.extensions?.type === 'AlreadyAsParticipant') {
      notificationStore.openToastNotification({
        message: fields.registrationUserAlreadyAssigned,
        type: 'error',
        icon: 'alert-triangle',
        persist: true,
      });
    } else if (r.error.graphQLErrors?.[0]?.extensions?.type === 'MaxParticipantReached') {
      notificationStore.openToastNotification({
        message: fields.registrationMaxParticipantReached,
        type: 'error',
        icon: 'alert-triangle',
        persist: true,
      });
    } else {
      notificationStore.openGeneralErrorNotification();
      console.error('Could not register users.', r.error);
    }
    return;
  }

  registrationSuccess.value = true;
  seminarRegistrationStore.reset();
};

const cancelRegistration = () => {
  seminarRegistrationStore.reset();
  emits('cancel');
};

const exitRegistration = () => {
  seminarRegistrationStore.reset();
  emits('exit');
};
</script>

<style scoped src="./seminar-registration-fi" />
